import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Share, SquarePlus, X, Download } from 'lucide-react';

/**
 * PWAInstallBanner — popup mobile "Installa l'app sul tuo cellulare".
 *
 * Android/Chrome: intercetta `beforeinstallprompt` e mostra un bottone
 * che apre il prompt nativo di installazione (icona sulla home screen).
 * iOS/Safari: il prompt nativo non esiste, quindi mostra le istruzioni
 * manuali (Condividi → "Aggiungi alla schermata Home").
 *
 * Non compare se l'app è già installata (display-mode standalone) e,
 * una volta chiuso, resta nascosto per DISMISS_DAYS giorni.
 */

const DISMISS_KEY = 'elios-pwa-install-dismissed';
const DISMISS_DAYS = 14;

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  // iPadOS 13+ si presenta come macOS ma ha il touch
  (navigator.userAgent.includes('Mac') && navigator.maxTouchPoints > 1);

const isMobile = () =>
  isIOS() || /Android/i.test(navigator.userAgent);

const isDismissed = () => {
  try {
    const ts = Number(localStorage.getItem(DISMISS_KEY));
    return ts && Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

export default function PWAInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isMobile() || isStandalone() || isDismissed()) return;

    const adopt = (e) => {
      setDeferredPrompt(e);
      setVisible(true);
    };

    // `beforeinstallprompt` viene emesso al caricamento iniziale, prima che
    // questo componente (dentro il layout autenticato) esista: index.js lo
    // mette da parte in window.__pwaInstallPrompt e lo segnala con l'evento
    // custom qui sotto. Il listener diretto resta per eventuali ri-emissioni.
    if (window.__pwaInstallPrompt) adopt(window.__pwaInstallPrompt);
    const onAvailable = () => {
      if (window.__pwaInstallPrompt) adopt(window.__pwaInstallPrompt);
    };
    window.addEventListener('pwa:install-available', onAvailable);

    const onBeforeInstall = (e) => {
      e.preventDefault();
      adopt(e);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    const onInstalled = () => setVisible(false);
    window.addEventListener('appinstalled', onInstalled);

    // Su iOS `beforeinstallprompt` non viene mai emesso: mostriamo il
    // banner con le istruzioni dopo un breve ritardo per non coprire
    // subito la UI al primo caricamento.
    let iosTimer;
    if (isIOS()) {
      iosTimer = setTimeout(() => setVisible(true), 3000);
    }

    return () => {
      window.removeEventListener('pwa:install-available', onAvailable);
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
      clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {}
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    window.__pwaInstallPrompt = null;
    if (outcome === 'accepted') setVisible(false);
    else dismiss();
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 28 }}
          className="lg:hidden fixed left-3 right-3 z-50 rounded-2xl bg-white border border-gray-200 shadow-[0_8px_30px_rgba(0,0,0,0.12)] p-4"
          style={{ bottom: 'calc(env(safe-area-inset-bottom) + 5rem)' }}
          role="dialog"
          aria-label="Installa l'app"
        >
          <button
            onClick={dismiss}
            aria-label="Chiudi"
            className="absolute top-2.5 right-2.5 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex items-start gap-3 pr-6">
            <img
              src="/pwa-icon-192.png"
              alt=""
              className="w-11 h-11 rounded-xl shrink-0"
            />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900">
                Installa l'app sul tuo cellulare
              </p>
              {deferredPrompt ? (
                <>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Aggiungi Elios alla schermata Home per un accesso rapido.
                  </p>
                  <button
                    onClick={install}
                    className="mt-2.5 inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3.5 py-2 text-xs font-semibold text-white active:bg-emerald-700"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Installa
                  </button>
                </>
              ) : (
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  Tocca{' '}
                  <Share className="w-3.5 h-3.5 inline align-[-2px] text-emerald-600" />{' '}
                  <span className="font-medium text-gray-700">Condividi</span> e poi{' '}
                  <SquarePlus className="w-3.5 h-3.5 inline align-[-2px] text-emerald-600" />{' '}
                  <span className="font-medium text-gray-700">
                    Aggiungi alla schermata Home
                  </span>
                </p>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
